/**
 * ⚠️  INTERNAL USE ONLY — NOT FOR PUBLIC ACCESS
 * Route: /admin/services
 * Password-gated. noindex. Not linked from any public page.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Download, Upload, Plus, Search,
  ChevronDown, ChevronUp, Pencil, Trash2, X, Check, AlertTriangle,
  RefreshCw, Filter, Package, Image, ExternalLink, Home, Zap, Shield,
  Edit3, Loader, FileText,
} from '../../lib/icons';
import { useImageSlots, saveImageSlot, uploadImageFile, invalidateSlotCache } from '../../lib/image-slots-client';
import type { ImageSlot } from '../../lib/image-slots-client';
import ImageEditor from '../../components/ImageEditor';
import { AdminLoginGate, isAdminAuthed } from '../../components/AdminLoginGate';
import type { ServiceEntry, ServiceStatus, DeviceCategory, RepairCategory } from '../../types/services';
import {
  getServices, updateService, addService, deleteService,
  exportJSON, importJSON, resetToInitial, triggerSheetSync,
} from '../../lib/services-store';
import { getCachedSheetData, syncAgeLabel, type SyncResult } from '../../lib/sheets-sync';
import { DEVICE_CATEGORY_LABELS, REPAIR_CATEGORY_LABELS } from '../../types/services';
import SEOHead from '../../components/SEOHead';


// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (p: number | null) =>
  p === null ? <span className="text-yellow-400 text-xs font-semibold">TBD</span>
             : <span>${p}</span>;

const STATUS_COLORS: Record<ServiceStatus, string> = {
  active:  'bg-green-500/20 text-green-400 border-green-500/40',
  draft:   'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
  hidden:  'bg-gray-500/20 text-gray-400 border-gray-500/40',
};

const BLANK_SERVICE: Omit<ServiceEntry, 'id' | 'createdAt' | 'updatedAt'> = {
  sku: '', status: 'draft', deviceCategory: 'ps5', deviceModels: [],
  repairCategory: 'repair', repairType: '', description: '',
  basePrice: null, rushPrice: null, priceTiers: [],
  turnaround: '', rushTurnaround: '', warranty: '90 days',
  mailInAvailable: true, internalNotes: '', disclaimer: '', tags: [],
  imageUrl: '', imageAlt: '',
  whatToBring: '', upsellIds: [], isUpsell: false,
};


// ── Image preview with error handling ─────────────────────────────────────────
const ImagePreview = ({ url, alt, className = '' }: { url: string; alt: string; className?: string }) => {
  const [err, setErr] = useState(false);
  useEffect(() => setErr(false), [url]);
  if (!url || err) return null;
  return (
    <img
      src={url}
      alt={alt || 'Service image'}
      loading="lazy"
      onError={() => setErr(true)}
      className={`rounded-lg object-cover ${className}`}
    />
  );
};

// ── Service form modal ────────────────────────────────────────────────────────
const ServiceModal = ({
  initial, onSave, onClose,
}: {
  initial: ServiceEntry | null;
  onSave: (s: ServiceEntry | Omit<ServiceEntry, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onClose: () => void;
}) => {
  const isNew = initial === null;
  const [form, setForm] = useState<Omit<ServiceEntry, 'id' | 'createdAt' | 'updatedAt'>>(
    initial ? { ...initial } : { ...BLANK_SERVICE }
  );
  const [imgErr, setImgErr] = useState(false);

  const set = <K extends keyof typeof form>(k: K, v: typeof form[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.repairType.trim()) return;
    if (isNew) onSave(form);
    else onSave({ ...(initial as ServiceEntry), ...form });
    onClose();
  };

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  // Reset img error when URL changes
  useEffect(() => setImgErr(false), [form.imageUrl]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-800 sticky top-0 bg-gray-900 z-10">
          <h2 className="text-[#E0E0E0] font-bold text-lg">{isNew ? 'Add Service' : 'Edit Service'}</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-400 hover:text-white" /></button>
        </div>

        <div className="p-6 space-y-4">

          {/* ── Image section ──────────────────────────────────────────────── */}
          <div className="border border-gray-700 rounded-xl p-4 space-y-3">
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide flex items-center gap-1.5">
              <Image className="h-3.5 w-3.5" /> Service Image
            </p>

            {/* Preview */}
            {form.imageUrl && !imgErr && (
              <div className="relative">
                <img
                  src={form.imageUrl}
                  alt={form.imageAlt || 'Preview'}
                  loading="lazy"
                  onError={() => setImgErr(true)}
                  className="w-full max-h-44 object-cover rounded-lg border border-gray-700"
                />
                <a href={form.imageUrl} target="_blank" rel="noopener noreferrer"
                  className="absolute top-2 right-2 bg-black/60 rounded p-1 text-gray-300 hover:text-white">
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            )}
            {form.imageUrl && imgErr && (
              <div className="bg-[#FF1744]/10 border border-[#FF1744]/30 rounded-lg p-3 text-xs text-[#FF1744]">
                ⚠ Image URL could not be loaded — check the path.
              </div>
            )}

            {/* URL input + clear */}
            <div className="flex gap-2">
              <input
                value={form.imageUrl}
                onChange={e => set('imageUrl', e.target.value)}
                placeholder="/images/services/ps5-hdmi.webp  or  https://..."
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]"
              />
              {form.imageUrl && (
                <button onClick={() => { set('imageUrl', ''); set('imageAlt', ''); }}
                  className="px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-gray-400 hover:text-[#FF1744] transition-colors text-sm">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Alt text */}
            <input
              value={form.imageAlt}
              onChange={e => set('imageAlt', e.target.value)}
              placeholder="Alt text — e.g. PS5 HDMI port repair"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]"
            />

            <p className="text-xs text-gray-600">
              Local images → save to <code className="text-gray-500">/public/images/services/</code> and use path <code className="text-gray-500">/images/services/filename.webp</code>
            </p>
          </div>

          {/* ── Row 1: SKU + Status ─────────────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">SKU</span>
              <input value={form.sku} onChange={e => set('sku', e.target.value)}
                className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]" />
            </label>
            <label className="block">
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Status</span>
              <select value={form.status} onChange={e => set('status', e.target.value as ServiceStatus)}
                className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]">
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="hidden">Hidden</option>
              </select>
            </label>
          </div>

          {/* ── Row 2: Category ─────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Device Category</span>
              <select value={form.deviceCategory} onChange={e => set('deviceCategory', e.target.value as DeviceCategory)}
                className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]">
                {(Object.entries(DEVICE_CATEGORY_LABELS) as [DeviceCategory, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Repair Category</span>
              <select value={form.repairCategory} onChange={e => set('repairCategory', e.target.value as RepairCategory)}
                className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]">
                {(Object.entries(REPAIR_CATEGORY_LABELS) as [RepairCategory, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Repair type */}
          <label className="block">
            <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Repair Type *</span>
            <input value={form.repairType} onChange={e => set('repairType', e.target.value)}
              placeholder="e.g. HDMI Port Replacement"
              className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]" />
          </label>

          <label className="block">
            <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Description (customer-facing)</span>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2}
              className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] resize-none" />
          </label>

          {/* What to bring */}
          <label className="block">
            <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">What to Bring (customer-facing)</span>
            <input value={form.whatToBring} onChange={e => set('whatToBring', e.target.value)}
              placeholder="e.g. PS5 console only. No cables or controllers needed."
              className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]" />
          </label>

          {/* Pricing row */}
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Base Price ($) — blank = TBD</span>
              <input type="number" min="0" step="1"
                value={form.basePrice ?? ''} onChange={e => set('basePrice', e.target.value === '' ? null : Number(e.target.value))}
                placeholder="TBD"
                className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]" />
            </label>
            <label className="block">
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Rush Price ($)</span>
              <input type="number" min="0" step="1"
                value={form.rushPrice ?? ''} onChange={e => set('rushPrice', e.target.value === '' ? null : Number(e.target.value))}
                placeholder="Optional"
                className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]" />
            </label>
          </div>

          {/* Turnaround / warranty */}
          <div className="grid grid-cols-3 gap-4">
            <label className="block">
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Turnaround</span>
              <input value={form.turnaround} onChange={e => set('turnaround', e.target.value)}
                placeholder="3-4 hours"
                className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]" />
            </label>
            <label className="block">
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Rush Turnaround</span>
              <input value={form.rushTurnaround} onChange={e => set('rushTurnaround', e.target.value)}
                placeholder="1-2 hours"
                className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]" />
            </label>
            <label className="block">
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Warranty</span>
              <input value={form.warranty} onChange={e => set('warranty', e.target.value)}
                placeholder="90 days"
                className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]" />
            </label>
          </div>

          {/* Checkboxes */}
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.mailInAvailable} onChange={e => set('mailInAvailable', e.target.checked)}
                className="w-4 h-4 rounded accent-[#39FF14]" />
              <span className="text-sm text-[#E0E0E0]">Mail-in available</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.isUpsell} onChange={e => set('isUpsell', e.target.checked)}
                className="w-4 h-4 rounded accent-[#00BFFF]" />
              <span className="text-sm text-[#E0E0E0]">Can appear as upsell / add-on</span>
            </label>
          </div>

          {/* Upsell IDs */}
          <label className="block">
            <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Suggest as Add-ons (comma-separated service IDs)</span>
            <input value={form.upsellIds.join(', ')}
              onChange={e => set('upsellIds', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
              placeholder="ps5-overheat, ps5-cleaning, ctrl-drift"
              className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]" />
            <p className="text-xs text-gray-600 mt-1">
              Service IDs listed here will appear as optional add-ons when this service is selected in the intake form.
            </p>
          </label>

          {/* Internal notes */}
          <label className="block">
            <span className="text-xs text-[#FF1744] font-semibold uppercase tracking-wide">⚠ Internal Notes (never shown to customers)</span>
            <textarea value={form.internalNotes} onChange={e => set('internalNotes', e.target.value)} rows={2}
              className="mt-1 w-full bg-gray-800 border border-[#FF1744]/40 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#FF1744] resize-none" />
          </label>

          <label className="block">
            <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Disclaimer (can show to customer)</span>
            <input value={form.disclaimer} onChange={e => set('disclaimer', e.target.value)}
              className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]" />
          </label>

          {/* Tags */}
          <label className="block">
            <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Tags (comma-separated)</span>
            <input value={form.tags.join(', ')}
              onChange={e => set('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
              placeholder="hdmi, ps5, no-video"
              className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14]" />
          </label>
        </div>

        <div className="flex gap-3 p-6 border-t border-gray-800 sticky bottom-0 bg-gray-900">
          <button onClick={handleSave}
            className="flex-1 bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2">
            <Check className="h-4 w-4" />
            {isNew ? 'Add Service' : 'Save Changes'}
          </button>
          <button onClick={onClose}
            className="px-6 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] font-semibold py-2.5 rounded-lg transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Image slot helpers ────────────────────────────────────────────────────────

const SECTION_LABELS: Record<string, string> = {
  'performance-upgrades': 'Performance Upgrades',
  'custom-controllers':   'Custom Controllers',
  'homepage':             'Homepage',
};

function getSlotSectionLabel(section: string) {
  return SECTION_LABELS[section] ?? section.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function SlotThumbnail({ src, alt }: { src: string | null; alt: string }) {
  const isVideo = src && (src.endsWith('.mp4') || src.endsWith('.mov') || src.endsWith('.webm'));
  if (!src) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-gray-950">
        <Image className="h-6 w-6 text-gray-700" />
        <p className="text-[10px] text-gray-600">No image</p>
      </div>
    );
  }
  if (isVideo) {
    return <video src={src} autoPlay muted loop playsInline className="w-full h-full object-cover" />;
  }
  return <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover" />;
}

function SlotCard({
  slot, onEdit, onReplace, onRemove,
}: {
  slot: ImageSlot;
  onEdit: (slot: ImageSlot) => void;
  onReplace: (slot: ImageSlot, file: File) => Promise<void>;
  onRemove: (slot: ImageSlot) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try { await onReplace(slot, file); } finally { setUploading(false); }
  };
  return (
    <div className="bg-gray-900 rounded-xl border border-gray-800 hover:border-gray-700 transition-all overflow-hidden flex flex-col">
      <div className="aspect-video bg-gray-950 overflow-hidden relative">
        <SlotThumbnail src={slot.image_url} alt={slot.alt_text || slot.label} />
        {uploading && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <Loader className="h-6 w-6 text-[#39FF14] animate-spin" />
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <p className="text-sm font-semibold text-[#E0E0E0] mb-0.5">{slot.label}</p>
        <p className="text-[10px] text-gray-600 font-mono mb-3">{slot.page_section} / {slot.slot_id}</p>
        <div className="flex flex-wrap gap-2 mt-auto">
          <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] border border-gray-700 cursor-pointer transition-all">
            <Upload className="h-3.5 w-3.5" />
            Replace
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={handleFileChange} />
          </label>
          <button onClick={() => onEdit(slot)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] border border-gray-700 transition-all">
            <Edit3 className="h-3.5 w-3.5" /> Edit
          </button>
          {slot.image_url && (
            <button onClick={() => onRemove(slot)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-950/40 hover:bg-red-900/50 text-red-400 border border-red-900/60 transition-all">
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main admin panel ──────────────────────────────────────────────────────────
const AdminPanel = () => {
  const [tab, setTab]             = useState<'services' | 'images'>('services');

  // ── Services state ──────────────────────────────────────────────────────────
  const [services, setServices]   = useState<ServiceEntry[]>(getServices());
  const [search, setSearch]       = useState('');
  const [catFilter, setCatFilter] = useState<DeviceCategory | 'all'>('all');
  const [statusFilter, setStatus] = useState<ServiceStatus | 'all'>('all');
  const [editTarget, setEdit]     = useState<ServiceEntry | null | 'new'>(null);
  const [expandedId, setExpanded] = useState<string | null>(null);
  const [toast, setToast]         = useState<string | null>(null);
  const importRef                 = useRef<HTMLInputElement>(null);

  // ── Images state ────────────────────────────────────────────────────────────
  const { slots, loading: slotsLoading, error: slotsError, refetch: refetchSlots } = useImageSlots();
  const [editingSlot, setEditingSlot] = useState<ImageSlot | null>(null);
  const [localSlots, setLocalSlots]   = useState<ImageSlot[] | null>(null);
  const displaySlots = localSlots ?? slots;
  const slotSections = Array.from(new Set(displaySlots.map(s => s.page_section))).sort();

  const updateLocalSlot = (updated: ImageSlot) => {
    setLocalSlots(prev => {
      const base = prev ?? slots;
      return base.map(s => s.slot_id === updated.slot_id ? updated : s);
    });
  };

  const handleSlotEdit = (slot: ImageSlot) => setEditingSlot(slot);

  const handleSlotEditorSave = async (updates: Partial<Omit<ImageSlot, 'slot_id' | 'updated_at'>>) => {
    if (!editingSlot) return;
    let imageUrl = updates.image_url;
    if (imageUrl && imageUrl.startsWith('blob:')) {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const file = new File([blob], 'upload.jpg', { type: blob.type || 'image/jpeg' });
      imageUrl = await uploadImageFile(editingSlot.slot_id, file);
      updates = { ...updates, image_url: imageUrl };
    }
    const updated = await saveImageSlot(editingSlot.slot_id, updates);
    updateLocalSlot(updated);
    invalidateSlotCache();
  };

  const handleSlotReplace = async (slot: ImageSlot, file: File) => {
    const url = await uploadImageFile(slot.slot_id, file);
    const updated = await saveImageSlot(slot.slot_id, { image_url: url });
    updateLocalSlot(updated);
    invalidateSlotCache();
  };

  const handleSlotRemove = async (slot: ImageSlot) => {
    if (!window.confirm(`Remove image from "${slot.label}"? This cannot be undone.`)) return;
    const updated = await saveImageSlot(slot.slot_id, { image_url: null });
    updateLocalSlot(updated);
    invalidateSlotCache();
  };

  // ── Sheet sync state ────────────────────────────────────────────────────────
  const sheetConfigured = !!import.meta.env.VITE_GOOGLE_SHEET_CSV_URL;
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'ok' | 'error'>('idle');
  const [syncError, setSyncError]   = useState<string>('');
  const [syncCache, setSyncCache]   = useState(() => getCachedSheetData());
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  const handleSheetSync = async () => {
    setSyncStatus('syncing');
    setSyncError('');
    setSyncResult(null);
    const result = await triggerSheetSync();
    if (result.ok) {
      setSyncStatus('ok');
      setSyncCache(getCachedSheetData());
      setServices(getServices());
      setSyncResult(result);
      showToast(`Synced ${result.services.length} services from sheet`);
    } else {
      setSyncStatus('error');
      setSyncError(result.error ?? 'Unknown error');
    }
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const filtered = useMemo(() => services.filter(s => {
    if (catFilter !== 'all' && s.deviceCategory !== catFilter) return false;
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return s.repairType.toLowerCase().includes(q) ||
             s.sku.toLowerCase().includes(q) ||
             s.deviceModels.join(' ').toLowerCase().includes(q) ||
             s.tags.join(' ').toLowerCase().includes(q);
    }
    return true;
  }), [services, catFilter, statusFilter, search]);

  const stats = useMemo(() => ({
    total:   services.length,
    active:  services.filter(s => s.status === 'active').length,
    draft:   services.filter(s => s.status === 'draft').length,
    hidden:  services.filter(s => s.status === 'hidden').length,
    tbd:     services.filter(s => s.basePrice === null && s.priceTiers.every(t => t.price === null)).length,
    images:  services.filter(s => s.imageUrl).length,
  }), [services]);

  const cycleStatus = (s: ServiceEntry) => {
    const next: ServiceStatus = s.status === 'active' ? 'hidden' : s.status === 'hidden' ? 'draft' : 'active';
    const updated = updateService({ ...s, status: next });
    setServices(updated);
    showToast(`${s.repairType} → ${next}`);
  };

  const handleSave = (data: ServiceEntry | Omit<ServiceEntry, 'id' | 'createdAt' | 'updatedAt'>) => {
    if ('id' in data) {
      setServices(updateService(data));
      showToast('Service updated');
    } else {
      setServices(addService(data));
      showToast('Service added');
    }
  };

  const handleDelete = (s: ServiceEntry) => {
    if (!window.confirm(`Delete "${s.repairType}"? This cannot be undone.`)) return;
    setServices(deleteService(s.id));
    showToast('Deleted');
  };

  const handleExport = () => {
    const blob = new Blob([exportJSON()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `gfs-services-${new Date().toISOString().slice(0, 10)}.json`;
    a.click(); URL.revokeObjectURL(url);
    showToast('Exported');
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const result = importJSON(ev.target?.result as string);
      if (result.ok) { setServices(getServices()); showToast(`Imported ${result.count} services`); }
      else showToast(`Import failed: ${result.error}`);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleReset = () => {
    if (!window.confirm('Reset ALL services to built-in defaults? All edits will be lost.')) return;
    setServices(resetToInitial());
    showToast('Reset to defaults');
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0]">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-gray-800 border border-[#39FF14]/40 text-[#39FF14] px-4 py-2 rounded-lg text-sm font-semibold shadow-xl">
          {toast}
        </div>
      )}

      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-[#FF1744]" />
          <div>
            <h1 className="font-bold text-[#E0E0E0]">Service Management</h1>
            <p className="text-xs text-[#FF1744] font-semibold tracking-wide">INTERNAL USE ONLY — GAMEFIX SOLUTIONS</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/repair-knowledge-base"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-[#E0E0E0] transition-colors">
            <FileText className="h-3.5 w-3.5 text-[#FF1744]" /> Repair KB
          </Link>
          <Link to="/admin/products"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-[#E0E0E0] transition-colors">
            <Package className="h-3.5 w-3.5 text-[#39FF14]" /> Products
          </Link>
          <Link to="/admin/gbp"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-[#E0E0E0] transition-colors">
            <ExternalLink className="h-3.5 w-3.5 text-[#FF1744]" /> GMB
          </Link>
          <Link to="/admin/phone-repair-preview"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A1628] hover:bg-[#0C1F3A] border border-[#1E3A5F] rounded-lg text-sm text-[#0EA5E9] transition-colors">
            📱 Phone Repair
          </Link>
          <Link to="/book-repair"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-[#E0E0E0] transition-colors">
            <Zap className="h-3.5 w-3.5 text-[#39FF14]" /> Book Repair
          </Link>
          <Link to="/admin/edit-site"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#39FF14]/10 hover:bg-[#39FF14]/20 border border-[#39FF14]/40 rounded-lg text-sm text-[#39FF14] font-semibold transition-colors">
            <Edit3 className="h-3.5 w-3.5" /> Edit Site
          </Link>
          <Link to="/"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-[#E0E0E0] transition-colors">
            <Home className="h-3.5 w-3.5" /> Main Site
          </Link>
          <button onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-[#E0E0E0] transition-colors">
            <Download className="h-3.5 w-3.5" /> Export
          </button>
          <button onClick={() => importRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-[#E0E0E0] transition-colors">
            <Upload className="h-3.5 w-3.5" /> Import
          </button>
          <input ref={importRef} type="file" accept=".json" onChange={handleImport} className="hidden" />
          <button onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-gray-400 transition-colors"
            title="Reset to built-in defaults">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => setEdit('new')}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold rounded-lg text-sm transition-colors">
            <Plus className="h-4 w-4" /> Add Service
          </button>
        </div>
      </header>

      {/* Tab bar */}
      <div className="flex border-b border-gray-800 bg-gray-900/40 px-6">
        {(['services', 'images'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-semibold transition-colors border-b-2 -mb-px capitalize ${
              tab === t
                ? 'border-[#39FF14] text-[#39FF14]'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            {t === 'images' && <Image className="h-3.5 w-3.5" />}
            {t === 'services' ? 'Services' : 'Images'}
          </button>
        ))}
      </div>

      {/* ── Services tab ── */}
      {tab === 'services' && <>

      {/* Google Sheet sync status bar */}
      {sheetConfigured ? (
        <div className={`px-6 py-2 border-b flex items-center gap-3 text-xs
          ${syncStatus === 'error'
            ? 'bg-[#FF1744]/10 border-[#FF1744]/30'
            : 'bg-gray-900/60 border-gray-800'}`}>
          {/* Status dot */}
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
            syncStatus === 'syncing' ? 'bg-yellow-400 animate-pulse'
            : syncStatus === 'error'   ? 'bg-[#FF1744]'
            : syncCache               ? 'bg-[#39FF14]'
            : 'bg-gray-600'}`} />

          {/* Label */}
          <span className="text-gray-400">
            {syncStatus === 'syncing' ? 'Syncing from Google Sheet…'
            : syncStatus === 'error'   ? <span className="text-[#FF1744]">Sync failed: {syncError}</span>
            : syncCache               ? <>Google Sheet · {syncCache.services.length} services · synced {syncAgeLabel(syncCache.fetchedAt)}</>
            : 'Google Sheet connected — not yet synced'}
          </span>

          {/* Sync Now button */}
          <button
            onClick={handleSheetSync}
            disabled={syncStatus === 'syncing'}
            className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded bg-gray-800 hover:bg-gray-700 border border-gray-700 text-[#E0E0E0] disabled:opacity-40 transition-colors">
            <RefreshCw className={`h-3 w-3 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
            Sync Now
          </button>

          {/* Link to sheet */}
          <a
            href={import.meta.env.VITE_GOOGLE_SHEET_CSV_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-gray-600 hover:text-[#39FF14] transition-colors">
            <ExternalLink className="h-3 w-3" /> Sheet
          </a>
        </div>
      ) : (
        <div className="px-6 py-2 border-b border-gray-800 bg-gray-900/40 flex items-center gap-2 text-xs text-gray-600">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-700 shrink-0" />
          Google Sheet not connected — add <code className="text-gray-500 bg-gray-800 px-1 rounded">VITE_GOOGLE_SHEET_CSV_URL</code> to .env to enable live sync
        </div>
      )}

      {/* Sync result details panel */}
      {syncResult && (() => {
        const CAT_LABELS: Partial<Record<string, string>> = {
          ps5: 'PlayStation', xbox: 'Xbox', switch: 'Nintendo',
          controller: 'Controller', 'laptop-macbook': 'Laptop/MacBook',
          'handheld-pc': 'Handheld PC', 'nintendo-handheld': 'Nintendo Handheld',
          iphone: 'iPhone', ipad: 'iPad', samsung: 'Samsung',
        };
        const catCounts: Record<string, number> = {};
        for (const s of syncResult.services) {
          catCounts[s.deviceCategory] = (catCounts[s.deviceCategory] ?? 0) + 1;
        }
        const skipReasons: Record<string, number> = {};
        for (const s of syncResult.skipped ?? []) {
          skipReasons[s.reason] = (skipReasons[s.reason] ?? 0) + 1;
        }
        const unknownCatRows = (syncResult.skipped ?? []).filter(s => s.reason === 'unknown_category');
        const unknownCatValues = [...new Set(unknownCatRows.map(s => s.raw))];
        const activeImported = syncResult.services.filter(s => s.status === 'active').length;

        return (
          <div className="mx-6 my-3 bg-gray-900 border border-gray-700 rounded-xl overflow-hidden text-xs">
            <div className="px-4 py-2 bg-gray-800/60 border-b border-gray-700 flex items-center justify-between">
              <span className="font-semibold text-[#E0E0E0]">Last Sync Results</span>
              <button onClick={() => setSyncResult(null)} className="text-gray-600 hover:text-gray-400">✕</button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-4">
              {/* Import summary */}
              <div>
                <div className="text-gray-500 font-medium mb-2 uppercase tracking-wide text-[10px]">Import Summary</div>
                <div className="space-y-1">
                  <div className="flex justify-between"><span className="text-gray-400">Sheet rows</span><span className="text-[#E0E0E0] font-semibold">{syncResult.rowCount}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Imported (active)</span><span className="text-green-400 font-semibold">{activeImported}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Skipped</span><span className={(syncResult.skipped?.length ?? 0) > 0 ? 'text-yellow-400 font-semibold' : 'text-gray-500'}>{syncResult.skipped?.length ?? 0}</span></div>
                </div>
                {(syncResult.skipped?.length ?? 0) > 0 && (
                  <div className="mt-3">
                    <div className="text-gray-500 font-medium mb-1.5 uppercase tracking-wide text-[10px]">Skip Reasons</div>
                    <div className="space-y-1">
                      {Object.entries(skipReasons).map(([reason, n]) => (
                        <div key={reason} className="flex justify-between">
                          <span className="text-gray-500">{reason.replace(/_/g, ' ')}</span>
                          <span className={reason === 'unknown_category' ? 'text-red-400 font-semibold' : 'text-yellow-400 font-semibold'}>{n}</span>
                        </div>
                      ))}
                    </div>
                    {unknownCatValues.length > 0 && (
                      <div className="mt-2 p-2 bg-red-950/40 border border-red-900/40 rounded text-red-400 text-[10px]">
                        Unknown categories: {unknownCatValues.join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Category breakdown */}
              <div>
                <div className="text-gray-500 font-medium mb-2 uppercase tracking-wide text-[10px]">By Category</div>
                <div className="space-y-1">
                  {Object.entries(catCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([cat, n]) => (
                      <div key={cat} className="flex justify-between">
                        <span className="text-gray-400">{CAT_LABELS[cat] ?? cat}</span>
                        <span className="text-[#E0E0E0] font-semibold">{n}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Stats bar */}
      <div className="grid grid-cols-6 divide-x divide-gray-800 border-b border-gray-800">
        {[
          { label: 'Total',      value: stats.total,   color: 'text-[#E0E0E0]' },
          { label: 'Active',     value: stats.active,  color: 'text-green-400' },
          { label: 'Draft',      value: stats.draft,   color: 'text-yellow-400' },
          { label: 'Hidden',     value: stats.hidden,  color: 'text-gray-400' },
          { label: 'Needs Price',value: stats.tbd,     color: 'text-[#FF1744]' },
          { label: 'Has Image',  value: stats.images,  color: 'text-[#00BFFF]' },
        ].map(({ label, value, color }) => (
          <div key={label} className="py-3 px-4 text-center">
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 px-6 py-4 border-b border-gray-800 bg-gray-900/50">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search services, SKU, tags…"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#39FF14]" />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <select value={catFilter} onChange={e => setCatFilter(e.target.value as DeviceCategory | 'all')}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#39FF14]">
            <option value="all">All Categories</option>
            {(Object.entries(DEVICE_CATEGORY_LABELS) as [DeviceCategory, string][]).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={e => setStatus(e.target.value as ServiceStatus | 'all')}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#39FF14]">
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
        <span className="text-xs text-gray-500">{filtered.length} of {services.length}</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-xs text-gray-500 uppercase tracking-wide">
              <th className="text-left px-4 py-3 w-10" />
              <th className="text-left px-4 py-3">SKU</th>
              <th className="text-left px-4 py-3">Service</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Models</th>
              <th className="text-right px-4 py-3">Price</th>
              <th className="text-left px-4 py-3">Turnaround</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-center px-4 py-3">Img</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => (
              <React.Fragment key={s.id}>
                <tr
                  className="border-b border-gray-800/60 hover:bg-gray-900/60 cursor-pointer transition-colors"
                  onClick={() => setExpanded(expandedId === s.id ? null : s.id)}
                >
                  {/* Image thumbnail */}
                  <td className="px-4 py-2">
                    {s.imageUrl ? (
                      <ImagePreview url={s.imageUrl} alt={s.imageAlt} className="w-8 h-8 shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded bg-gray-800 flex items-center justify-center">
                        <Image className="h-3 w-3 text-gray-600" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-400">{s.sku || '—'}</td>
                  <td className="px-4 py-3 font-medium text-[#E0E0E0] max-w-[200px]">
                    <div className="flex items-center gap-2">
                      {expandedId === s.id ? <ChevronUp className="h-3 w-3 text-gray-400 shrink-0" /> : <ChevronDown className="h-3 w-3 text-gray-400 shrink-0" />}
                      <span className="truncate">{s.repairType}</span>
                      {s.isUpsell && <span className="text-[0.6rem] px-1 py-0.5 bg-[#00BFFF]/20 text-[#00BFFF] rounded font-semibold shrink-0">UPSELL</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{DEVICE_CATEGORY_LABELS[s.deviceCategory]}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs max-w-[140px] truncate">
                    {s.deviceModels.length === 0 ? 'All models' : s.deviceModels.join(', ')}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">
                    {s.priceTiers.length > 0
                      ? <span className="text-xs text-[#00BFFF]">{s.priceTiers.length} tiers</span>
                      : fmt(s.basePrice)}
                  </td>
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{s.turnaround || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={e => { e.stopPropagation(); cycleStatus(s); }}
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_COLORS[s.status]} hover:opacity-80 transition-opacity`}>
                      {s.status}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {s.imageUrl
                      ? <Check className="h-4 w-4 text-[#00BFFF] mx-auto" />
                      : <X className="h-4 w-4 text-gray-700 mx-auto" />}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={e => { e.stopPropagation(); setEdit(s); }}
                        className="p-1.5 hover:bg-gray-700 rounded text-gray-400 hover:text-[#39FF14] transition-colors">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={e => { e.stopPropagation(); handleDelete(s); }}
                        className="p-1.5 hover:bg-gray-700 rounded text-gray-400 hover:text-[#FF1744] transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Expanded row */}
                {expandedId === s.id && (
                  <tr className="border-b border-gray-700 bg-gray-900/40">
                    <td colSpan={10} className="px-8 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-sm">
                        {/* Image preview */}
                        {s.imageUrl && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Image</p>
                            <ImagePreview url={s.imageUrl} alt={s.imageAlt} className="w-full max-h-36 rounded-lg" />
                            {s.imageAlt && <p className="text-xs text-gray-600 mt-1">{s.imageAlt}</p>}
                          </div>
                        )}

                        <div className={s.imageUrl ? '' : 'md:col-span-1'}>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Description</p>
                          <p className="text-[#B0B0B0]">{s.description || '—'}</p>
                          {s.disclaimer && (
                            <p className="text-xs text-yellow-400 mt-2">ℹ {s.disclaimer}</p>
                          )}
                          {s.whatToBring && (
                            <p className="text-xs text-[#00BFFF] mt-2">🎒 {s.whatToBring}</p>
                          )}
                        </div>

                        <div>
                          {s.priceTiers.length > 0 && (
                            <>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Price Tiers</p>
                              <div className="space-y-1">
                                {s.priceTiers.map((t, i) => (
                                  <div key={i} className="flex justify-between text-xs">
                                    <span className="text-[#B0B0B0]">{t.label}</span>
                                    <span className="font-semibold">{fmt(t.price)}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          {s.rushPrice && (
                            <p className="text-xs text-[#00BFFF] mt-2">Rush: ${s.rushPrice}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-2">Warranty: {s.warranty || '—'}</p>
                          <p className="text-xs text-gray-500">Tags: {s.tags.join(', ') || '—'}</p>
                          {s.upsellIds.length > 0 && (
                            <p className="text-xs text-[#00BFFF] mt-2">Upsells: {s.upsellIds.join(', ')}</p>
                          )}
                        </div>

                        <div>
                          {s.internalNotes && (
                            <div className="bg-[#FF1744]/10 border border-[#FF1744]/30 rounded-lg p-3">
                              <p className="text-xs text-[#FF1744] font-semibold uppercase tracking-wide mb-1">⚠ Internal Notes</p>
                              <p className="text-[#B0B0B0] text-xs">{s.internalNotes}</p>
                            </div>
                          )}
                          <p className="text-xs text-gray-600 mt-2">ID: {s.id}</p>
                          <p className="text-xs text-gray-600">Updated: {new Date(s.updatedAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}

            {filtered.length === 0 && (
              <tr>
                <td colSpan={10} className="px-6 py-16 text-center text-gray-600">
                  <Package className="h-8 w-8 mx-auto mb-3 opacity-40" />
                  No services match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-800 flex items-center gap-2 text-xs text-gray-600">
        <AlertTriangle className="h-3.5 w-3.5 text-[#FF1744]" />
        Internal tool — data stored in browser localStorage. Export regularly to back up.
        Data marked <span className="text-yellow-400 mx-1">TBD</span> will not show public pricing until a price is set and status is Active.
        Service images → save to <code className="mx-1 text-gray-500">/public/images/services/</code>
      </div>

      {/* Services modal */}
      {editTarget !== null && (
        <ServiceModal
          initial={editTarget === 'new' ? null : editTarget}
          onSave={handleSave}
          onClose={() => setEdit(null)}
        />
      )}

      </>}

      {/* ── Images tab ── */}
      {tab === 'images' && (
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-12">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-500">Upload and manage images for site sections.</p>
            <button
              onClick={refetchSlots}
              className="text-xs text-gray-500 hover:text-[#E0E0E0] flex items-center gap-1 transition-colors"
            >
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>

          {slotsLoading && (
            <div className="flex items-center justify-center py-20 gap-3 text-gray-500">
              <Loader className="h-5 w-5 animate-spin" />
              <span>Loading image slots…</span>
            </div>
          )}

          {slotsError && (
            <div className="bg-red-950/40 border border-red-800 rounded-xl p-4 text-red-400 text-sm">
              {slotsError}
            </div>
          )}

          {!slotsLoading && slotSections.map(section => {
            const sectionSlots = displaySlots.filter(s => s.page_section === section);
            return (
              <div key={section}>
                <div className="flex items-center gap-4 mb-6">
                  <h2 className="text-xl font-bold text-[#E0E0E0]">{getSlotSectionLabel(section)}</h2>
                  <div className="h-px flex-1 bg-gray-800" />
                  <span className="text-xs text-gray-600">{sectionSlots.length} slots</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {sectionSlots.map(slot => (
                    <SlotCard
                      key={slot.slot_id}
                      slot={slot}
                      onEdit={handleSlotEdit}
                      onReplace={handleSlotReplace}
                      onRemove={handleSlotRemove}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {!slotsLoading && displaySlots.length === 0 && !slotsError && (
            <div className="text-center py-20 text-gray-600">
              <Image className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No image slots found. Run the migration to seed slots.</p>
            </div>
          )}
        </div>
      )}

      {/* Image editor modal */}
      {editingSlot && (
        <ImageEditor
          slot={editingSlot}
          onSave={handleSlotEditorSave}
          onClose={() => setEditingSlot(null)}
        />
      )}
    </div>
  );
};

// ── Page entry point (auth wrapper) ──────────────────────────────────────────
const AdminServicesPage = () => {
  const [authed, setAuthed] = useState(isAdminAuthed());

  return (
    <>
      <SEOHead
        title="Admin — Service Management | GameFix Solutions"
        description=""
        noIndex
      />
      {authed ? <AdminPanel /> : <AdminLoginGate onAuth={() => setAuthed(true)} />}
    </>
  );
};

export default AdminServicesPage;
